/**
 * @file Radar SDK
 * @author guwanpo
 */

import RadarSDK, { RadarOptions as RadarSDKOptions } from '@ks-radar/radar';
import type { HybridWeblogInstance } from '@/types/index';
import { BasePlugin } from '../base';
import { error, warn } from '@/util/console';

export interface TimelineOption {
    sendTimeline?: boolean;
    timestamp?: number;
}

export interface EventDimension {
    name: string;
    category?: string | string[];
    event_type?: string | string[];
    src?: string;
    result_type?: string;
    message?: string;
    extra_info?: object | string;
    yoda_version?: string;
    webview_type?: string;
}

export interface ActionStartDimension {
    name: string;
    extra_info?: object;
}
export interface ActionEndDimension {
    name: string;
    extra_info?: object;
    result_type?: string;
}

export interface SetDimensionParam {
    c_dimension1?: any;
    c_dimension2?: any;
    c_dimension3?: any;
}

export interface APIHooKData {
    request: any;
    response: any;
    duration: number;
}

export interface APIHooKResData {
    intercept_report?: boolean;
    response_code?: string | number;
    response_msg?: any;
    status?: string | number;
    custom_failed?: boolean;
}

export interface ResourceHookData {
    file: string;
    cached: boolean;
    protocol: string;
    res_type: string;
    failed: boolean;
}

export interface ResourceHookResData {
    intercept_report: boolean;
}

export interface RadarOptions extends RadarSDKOptions {
    eventSampling?: number;
}

const CUSTOM_COLLECT_KEY: string[] = [];

export default class Radar extends BasePlugin {
    static override key = 'radar';
    public options: RadarOptions;
    public radarInstance?: RadarSDK;
    // 关键动作上报
    public action?: {
        start: (dimension: ActionStartDimension) => void;
        end: (dimension: ActionEndDimension) => void;
    };
    /**
     * 兼容写法，如果在 Weblog 初始化设置 plugins 时，不需要设置 weblog
     * @param weblog
     * @param options
     */
    constructor(options: RadarOptions);
    constructor(weblog: HybridWeblogInstance, options: RadarOptions);
    constructor(weblog: HybridWeblogInstance | RadarOptions, options?: RadarOptions) {

        super();
        // 有options证明是用配置的方式初始化的，否则是new的方式初始化的，将第一个参数给this.options
        this.options = options ? options : weblog as RadarOptions;
        // 验证配置内容
        if (!this.validOption()) {
            return;
        }
        // 配置方式初始化radar时，weblogger会把weblogger对象传进来，而且不会调用apply方法，这里我们需要手动调用一下
        // https://git.corp.kuaishou.com/ks-frontend/modules/webLogger/-/blob/master/src/entries/browser-full.ts#L49
        if ((weblog as HybridWeblogInstance)?.collect) {
            this.apply(weblog as HybridWeblogInstance);
        }
    }

    override apply(weblog: HybridWeblogInstance): void {
        this.weblog = weblog;
        this.radarInstance = new RadarSDK({
            ...this.options,
            weblog,
        });
        this.action = this.radarInstance?.action
    }

    destroy(): void {
        this.radarInstance?.destroy();
    }

    public collect(key: string, duration?: number) {
        if (key.indexOf('c_') !== 0 && CUSTOM_COLLECT_KEY.indexOf(key) < 0) {
            error('[error 305]', `[Radar] 传入的自定义指标 ${key} 必须为 c_ 开头`);
            return;
        }

        warn('[radar]:Radar+之后将不再支持collect方法，请用event方法替代。');
        this.radarInstance?.event({ name: key }, { duration });
    }

    public fmp(time?: number | {
        timestamp?: number;
    }) {
        this.radarInstance?.fmp(time);
    }

    public event(dimension: EventDimension, value?: object) {
        this.radarInstance?.event(dimension, value);
    }

    public customStage(name: string, options?: TimelineOption) {
        this.radarInstance?.customStage(name, options);
    }

    // 用于无头浏览器环境手动模拟onload事件内容进行perf数据收集
    public headlessTestReport() {
        warn('[radar]:Radar+已不支持不再支持headlessTestReport方法');
    }

    public playClicked(time?: number) {
        warn('[radar]:Radar+已不支持不再支持playClicked方法');
    }

    public playIDR(time?: number) {
        warn('[radar]:Radar+已不支持不再支持playIDR方法');
    }

    public playBlock(time?: string) {
        warn('[radar]:Radar+已不支持不再支持playBlock方法');
    }

    public playError(time?: string) {
        warn('[radar]:Radar+已不支持不再支持playError方法');
    }

    public setDimensions(param: SetDimensionParam) {
        this.radarInstance?.setDimensions(param);
    }

    validOption() {
        if (!this.options) {
            // 未传入参数默认不开启
            return false;
        }

        const { sampling = 1, projectId } = this.options;
        if (typeof projectId !== 'string') {
            // error('[error 303]', '[Radar] 必须配置 projectId 属性，类型为 string!');
            return false;
        }
        if (sampling > 1 || sampling < 0) {
            // error('[error 304]', '[Radar] 采样率 sampling 必须为 0~1 之间的数字');
            return false;
        }
        ([
            'apiSampling', 'errorSampling', 'resourceSampling', 'eventSampling',
        ] as const).forEach(optionName => {
            const sampling = this.options![optionName];
            if (sampling === undefined) {
                return;
            }
            if (typeof sampling === 'number' && sampling === 0) {
                error('[error 304]', `[Radar] 采样率 ${optionName} 不能被设置为 0，将改用默认值`);
            }
        });

        return true;
    }

    /**
     * 用于主动记录某个错误，并在雷达后台中跟踪它们。
     *
     * ## 场景 一
     *
     * 对于使用 try...catch... 捕获的错误，若想要记录并跟踪它们，则可以使用该方法：
     *
     * ```javascript
     * try {
     *     doSomething();
     * } catch (error) {
     *     weblog.plugins.radar.captureException(error);
     * }
     * ```
     *
     * ## 场景 二
     *
     * 用于记录 Vue 组件中的未捕获异常：
     *
     * ```javascript
     * Vue.config.errorHandler = function (error) {
     *     weblog.plugins.radar.captureException(error);
     * }
     * ```
     *
     * @param error - 错误信息，最好是传入一个 Error 对象，因为这可以让我们记录下该错误发生的地点以及其它有用的信息。
     */
    public captureException(error: Error): void {
        this.radarInstance?.captureException(error);
    }
}
